import PropTypes from "prop-types";
import React from "react";
import axios from "axios";
import { SeatsioSeatingChart } from "@seatsio/seatsio-react";
import { Modal, Button, Alert, Spinner } from "@opendate-app/setlist";
import Payment from "../../../storefronts/shared/Payment";

const money = (amount) => `$${Number(amount).toFixed(2)}`;
const firstError = (error) => error?.response?.data?.errors?.[0] || "Something went wrong. Please try again.";

const FanUpgradeModal = ({ ticketId, csrfToken, onClose }) => {
  const [offer, setOffer] = React.useState(null);
  const [seat, setSeat] = React.useState(null);
  const [checkout, setCheckout] = React.useState(null);
  const [processing, setProcessing] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const headers = { "X-CSRF-TOKEN": csrfToken };
  const base = `/my_orders/ticket_upgrades/${ticketId}`;

  React.useEffect(() => {
    axios.get(base).then((r) => setOffer(r.data)).catch((e) => setError(firstError(e)));
  }, [base]);

  const start = (choice) => {
    setBusy(true);
    setError(null);
    axios.post(`${base}/start`, choice, { headers })
      .then((r) => setCheckout(r.data))
      .catch((e) => setError(firstError(e)))
      .finally(() => setBusy(false));
  };

  const pickSeat = (object, holdToken) => {
    setSeat(null);
    setError(null);
    axios.post(`${base}/quote`, { seat: object.id, hold_token: holdToken }, { headers })
      .then((r) => setSeat({ id: object.id, label: object.label, holdToken, priceDifference: r.data.priceDifference }))
      .catch((e) => setError(firstError(e)));
  };

  const finish = () => axios.get(checkout.completePath)
    .then((r) => setProcessing(r.data.message))
    .catch((e) => {
      setFailed(true);
      setError(firstError(e));
    });

  let body;
  if (processing) {
    body = <Alert color="success" title={processing} />;
  } else if (failed) {
    body = null;
  } else if (checkout) {
    body = (
      <>
        <p>Total to pay: {money(checkout.total)}</p>
        <Payment
          publishableKey={checkout.publishableKey}
          connectedAccountId={checkout.connectedAccountId}
          clientSecret={checkout.clientSecret}
          label={`Pay ${money(checkout.total)}`}
          returnUrl={checkout.returnUrl}
          onPaid={finish}
          elementOptions={{ wallets: { link: "never" } }}
        />
      </>
    );
  } else if (!offer) {
    body = !error && <Spinner />;
  } else {
    body = (
      <>
        {offer.typeOptions.map((option) => (
          <Button key={option.ticketTypeId} variant="secondary" disabled={busy} onClick={() => start({ ticket_type_id: option.ticketTypeId })}>
            {`${option.name}: ${money(option.priceDifference)} + fees`}
          </Button>
        ))}
        {offer.seating && offer.seating.sections.map((section) => (
          <p key={section.ticketTypeId}>{`${section.name}: ${money(section.priceDifference)} + fees`}</p>
        ))}
        {offer.seating && (
          <div style={{ height: 400 }}>
            <SeatsioSeatingChart
              workspaceKey={offer.seating.publicKey}
              event={offer.seating.eventKey}
              region="na"
              session="start"
              maxSelectedObjects={1}
              availableCategories={offer.seating.sections.map((section) => section.categoryKey)}
              onObjectSelected={(object) => pickSeat(object, object.chart.holdToken)}
              onObjectDeselected={() => setSeat(null)}
            />
          </div>
        )}
        {seat && (
          <Button variant="primary" disabled={busy} onClick={() => start({ seat: seat.id, hold_token: seat.holdToken })}>
            {`Upgrade to ${seat.label}: ${money(seat.priceDifference)} + fees`}
          </Button>
        )}
        {offer.typeOptions.length === 0 && !offer.seating && <p>There are no upgrades for this ticket right now.</p>}
      </>
    );
  }

  return (
    <Modal open onClose={processing ? () => window.location.reload() : onClose} title="Upgrade your ticket" size="lg">
      <div style={{ display: "grid", gap: 12 }}>
        {error && <Alert color="error" title={error} />}
        {body}
      </div>
    </Modal>
  );
};

FanUpgradeModal.propTypes = {
  ticketId: PropTypes.number.isRequired,
  csrfToken: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FanUpgradeModal;
